/**
 * multi dimensional maps use stringified properties of an object as the key
 * of an internal, simple, map
 *
 * the {@link keyFn} should produce unique keys for all objects that may appear
 * in the map as to not overwrite things unintentionally
 */
export class MultiDimensionalMap<T> {
  private map = new Map<string, T>()
  private keyFn: MultiDimensionalMapKeyFn<T>
  /**
   * @param keyFn a function to resolve properties of {@link T} into a string for use in the internal map
   * @see {@link MultiDimensionalMap}
   * @see {@link MultiDimensionalMapKeyFn}
   */
  constructor(keyFn: MultiDimensionalMapKeyFn<T>)
  /**
   * @param map a previously defined map to copy into this map
   * @see {@link MultiDimensionalMap}
   */
  constructor(map: MultiDimensionalMap<T>)
  constructor(arg: MultiDimensionalMapKeyFn<T> | MultiDimensionalMap<T>) {
    if (typeof arg === 'function') this.keyFn = arg
    else {
      this.keyFn = arg.keyFn
      this.map = arg.map
    }
  }
  /**
   * attempts to get the value from the map
   *
   * @param value
   * @throws if {@link keyFn} throws an error due to missing properties of value
   * @see {@link keyFn}
   */
  get(value: Partial<T>) {
    try {
      return this.map.get(this.keyFn(value as T))
    } catch (err) {
      console.error(err)
      return undefined
    }
  }
  /**
   * adds, or replaces an existing, value in the map
   *
   * @param value
   * @see {@link keyFn}
   */
  set(value: T) {
    this.map.set(this.keyFn(value), value)
    return this
  }
  /**
   * returns the resolved key for the value
   *
   * @param value
   * @throws if {@link keyFn} throws an error due to missing properties of value
   * @see {@link keyFn}
   */
  key(value: Partial<T>) {
    return this.keyFn(value as T)
  }
  /**
   * returns the internal map's entries
   */
  entries() {
    return this.map.entries()
  }
  /**
   * returns the internal map's values
   */
  values() {
    return this.map.values()
  }
  /**
   * returns the internal map's keys
   */
  keys() {
    return this.map.keys()
  }
  /**
   * returns the current size of the internal map
   */
  get size() {
    return this.map.size
  }
  /**
   * removes a value from the map
   *
   * @param value
   * @throws if {@link keyFn} throws an error
   * @see {@link keyFn}
   */
  delete(value: Partial<T>) {
    try {
      return this.map.delete(this.keyFn(value as T))
    } catch (err) {
      console.error(err)
      return false
    }
  }
  /**
   * iterates over each value and key of the internal map using the supplied
   * callback function
   *
   * @param callbackFn
   */
  forEach(
    callbackfn: (value: T, key: string, map: Map<string, T>) => void,
    thisArg?: any
  ) {
    return this.map.forEach(callbackfn, thisArg)
  }
  /**
   * removes all values from the internal map
   */
  clear() {
    return this.map.clear()
  }
  /**
   * checks to see if the value can be found in the internal map
   *
   * @param value
   * @throws if {@link keyFn} throws an error
   * @see {@link keyFn}
   */
  has(value: Partial<T>) {
    try {
      return this.map.has(this.keyFn(value as T))
    } catch (err) {
      console.error(err)
      return false
    }
  }
}

type MultiDimensionalMapKeyFn<T> = (value: T) => string
